// Screens for the Cercle PWA. Each function returns its root element
// synchronously — async data is loaded in the background and swapped into a
// skeleton placeholder, so the user never sees a blank flash or a layout jump
// (brief: "États de chargement explicites"). Navigation goes through the
// hash router, so every view is deep-linkable and survives a refresh.

import { h } from './dom';
import { toast } from './toast';
import { currentIdentity, recoverIdentity, wipeLocal } from '../core/identity';
import { emptyProfile, fullName, getMyRecord, saveMyProfile, type Address, type Profile, type ProfileRecord } from '../core/profile';
import { getFriend, listFriends, setFriendGroups, setTempName, setVerified, type Friend } from '../core/friends';
import { createGroup, deleteGroup, groupCounts, listGroups, renameGroup, type Group } from '../core/groups';
import { decodeCircleId, importEcdhPublic } from '../core/keys';
import { computeFingerprint } from '../core/fingerprint';
import { lastStorageStatus } from '../core/persistence';
import { isStandalone } from '../core/installPrompt';
import { openInstallSheet } from './installSheet';
import { getCachedProfile } from '../core/cache';
import { publishMyProfile, publishSelfBackup, restoreMyProfileFromRelay, revokeFriend } from '../core/sync';
import { requestSync } from '../core/syncManager';
import { withPullToRefresh } from './pullToRefresh';
import { openAddFriendSheet } from './addFriendSheet';
import { openHowItWorksSheet } from './howItWorksSheet';
import { icon } from './icons';
import { navigate, pendingInvite, setPendingInvite, takePendingInvite, type Screen } from './router';
import { clearDraft, loadDraft, saveDraft } from './drafts';
import { OnboardingScreen } from './onboarding';
import {
  appShell,
  avatarEl,
  brand,
  bullet,
  card,
  centered,
  field,
  ghostButton,
  mainHeader,
  pageHeader,
  primaryButton,
  shareCard,
  skeletonCard,
  syncIndicator,
} from './components';

export type { Screen };

function shortId(circleId: string): string {
  return `${circleId.slice(0, 8)}…${circleId.slice(-6)}`;
}

function sectionTitle(text: string): HTMLElement {
  return h('h2', { class: 'section-title', text });
}

function lead(text: string): HTMLElement {
  return h('p', { class: 'lead', text });
}

function notice(kind: 'warn' | 'danger', text: string, action?: HTMLElement): HTMLElement {
  return h('div', { class: `notice notice-${kind}` }, h('span', { text }), action ?? null);
}

function addressText(a: Address): string {
  return [a.lines, a.country].filter(Boolean).join('\n');
}

/** Address label for display — the entry's own label, or a numbered fallback
 * when there's more than one and it wasn't given one. */
function addressLabel(a: Address, i: number, total: number): string {
  if (a.label) return `Adresse (${a.label})`;
  return total > 1 ? `Adresse ${i + 1}` : 'Adresse';
}

/** Display-only rendering of a stored dob (YYYY/MM/DD) in the reader's own
 * locale, e.g. "7 novembre 1867". The edit form keeps the raw YYYY/MM/DD
 * value — only read-only views get the localised long form. */
function formatDob(dob: string): string {
  const m = /^(\d{4})\/(\d{2})\/(\d{2})$/.exec(dob);
  if (!m) return dob;
  const [, y, mo, d] = m;
  const date = new Date(Number(y), Number(mo) - 1, Number(d));
  if (Number.isNaN(date.getTime())) return dob;
  return new Intl.DateTimeFormat(navigator.language, { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
}

/** Stored dob (YYYY/MM/DD) shown in the edit form as JJ/MM/AAAA. Falls back
 * to the raw value when it doesn't match, so malformed legacy data stays
 * editable instead of silently disappearing. */
function dobToDisplay(dob: string): string {
  const m = /^(\d{4})\/(\d{2})\/(\d{2})$/.exec(dob);
  if (!m) return dob;
  const [, y, mo, d] = m;
  return `${d}/${mo}/${y}`;
}

/** Inverse of dobToDisplay, for saving the edit form's JJ/MM/AAAA back to
 * the stored YYYY/MM/DD convention. */
function dobFromDisplay(display: string): string {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(display);
  if (!m) return display;
  const [, d, mo, y] = m;
  return `${y}/${mo}/${d}`;
}

/** Reformats a JJ/MM/AAAA input's raw text as digits are typed, inserting
 * the '/' separators automatically. */
function formatDobInput(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  const day = digits.slice(0, 2);
  const month = digits.slice(2, 4);
  const year = digits.slice(4, 8);
  return [day, month, year].filter(Boolean).join('/');
}

// --- Welcome / onboarding ---------------------------------------------------

/**
 * The no-identity entry point. A first-time visitor gets the full onboarding
 * (see ./onboarding); someone who already has a Cercle takes the "J'ai déjà une
 * phrase de récupération" exit from its first screen. Kept under this name
 * because several screens fall back to it when no identity is loaded.
 */
export function WelcomeScreen(): HTMLElement {
  return OnboardingScreen();
}

// --- Recover (returning user) -----------------------------------------------

const RECOVERY_WORDS = [12, 24];

export function RecoverScreen(): HTMLElement {
  const input = h('textarea', {
    rows: '4',
    placeholder: 'Vos 12 ou 24 mots, séparés par des espaces',
    autocapitalize: 'none',
    autocomplete: 'off',
    spellcheck: 'false',
    'aria-label': 'Phrase de récupération',
  }) as HTMLTextAreaElement;

  // Live word count: the one thing that actually goes wrong when typing 12
  // words by hand, surfaced before the button is even reachable.
  const count = h('span', { class: 'word-count', text: '0 / 12 mots' });
  const errorSlot = h('div', { class: 'recover-error', hidden: 'hidden' });

  const words = (): string[] => input.value.trim().split(/\s+/).filter(Boolean);
  const submit = primaryButton('Retrouver mon Cercle', () => void run(), 'key');
  submit.disabled = true;

  const sync = (): void => {
    const n = words().length;
    const ok = RECOVERY_WORDS.includes(n);
    count.textContent = `${n} / ${n > 12 ? 24 : 12} mots`;
    count.classList.toggle('is-ok', ok);
    submit.disabled = !ok;
  };
  input.addEventListener('input', () => {
    errorSlot.hidden = true;
    sync();
  });

  let busy = false;
  const run = async (): Promise<void> => {
    if (busy) return;
    busy = true;
    submit.disabled = true;
    try {
      const id = await recoverIdentity(input.value);
      // The phrase restores the keys; the encrypted self-backup on the relay
      // restores the profile, contacts and groups (best-effort — the relay
      // may be off, or there may be nothing backed up yet).
      try {
        const restored = await restoreMyProfileFromRelay(id);
        toast(restored ? 'Content de vous revoir — vos données ont été restaurées' : 'Identité récupérée');
      } catch {
        toast('Identité récupérée');
      }
      navigate(pendingInvite() ? { name: 'circle' } : { name: 'dashboard' });
    } catch (e) {
      busy = false;
      submit.disabled = false;
      errorSlot.replaceChildren(h('span', { text: (e as Error).message }));
      errorSlot.hidden = false;
      input.focus();
    }
  };

  return centered(
    brand(),
    h('h1', { class: 'hero-title', text: 'Content de vous revoir' }),
    lead(
      'Saisissez vos 12 ou 24 mots, dans l’ordre. Tout se reconstitue sur cet appareil : votre phrase n’est envoyée nulle part.',
    ),
    card(input, h('div', { class: 'recover-meta' }, count), errorSlot, submit),
    h('p', {
      class: 'small muted center',
      text: 'Vos proches, vos groupes et vos coordonnées reviennent tout seuls, dans les secondes qui suivent.',
    }),
    ghostButton('Retour', () => navigate({ name: 'welcome' }), 'back'),
  );
}

// --- Dashboard --------------------------------------------------------------

/** Value row inside the identity card (label above a left-aligned value). */
function idField(label: string, value: string): HTMLElement | null {
  if (!value) return null;
  return h('div', { class: 'id-field' }, h('span', { class: 'field-label', text: label }), h('div', { class: 'field-value', text: value }));
}

function greeting(): string {
  const hr = new Date().getHours();
  if (hr < 5) return 'Bonne nuit';
  if (hr < 18) return 'Bonjour';
  return 'Bonsoir';
}

export function DashboardScreen(): HTMLElement {
  const me = currentIdentity();
  if (!me) return WelcomeScreen();

  // A gear on the title line is the app's settings entry point; destructive and
  // rarely-used actions (like wiping the device) live inside it, off the profile.
  const settingsBtn = h(
    'button',
    { class: 'icon-btn', 'aria-label': 'Réglages', title: 'Réglages', onclick: () => navigate({ name: 'settings' }) },
    icon('settings', 22),
  );
  const header = mainHeader('Mon profil', greeting(), settingsBtn);

  const HOME_INTRO_KEY = 'circle:home-intro-dismissed';
  let introDismissed = false;
  try {
    introDismissed = localStorage.getItem(HOME_INTRO_KEY) === '1';
  } catch {
    /* ignore */
  }
  let introCard: HTMLElement | false = false;
  if (!introDismissed) {
    const dismissBtn = h(
      'button',
      {
        class: 'icon-btn home-intro-close',
        'aria-label': 'Fermer cette présentation',
        title: 'Fermer cette présentation',
        onclick: () => {
          try {
            localStorage.setItem(HOME_INTRO_KEY, '1');
          } catch {
            /* ignore */
          }
          if (introCard) introCard.remove();
        },
      },
      icon('close', 16),
    );
    introCard = h(
      'div',
      { class: 'card home-intro' },
      dismissBtn,
      h('p', {
        text: 'Cercle est un carnet d’adresses vivant : vous tenez votre fiche à jour une fois, vos proches la reçoivent aussitôt. Chiffré, sans compte ni mot de passe.',
      }),
      ghostButton('Comment ça marche ?', openHowItWorksSheet, 'info'),
    );
  }

  const profileSlot = skeletonCard(4);
  void getMyRecord().then((record) => {
    const p = record.profile;
    const name = fullName(p) || 'Fiche incomplète';
    // Personalise the greeting once the first name is known.
    if (p.firstName) {
      const title = header.querySelector('.page-title');
      if (title) title.textContent = `${greeting()}, ${p.firstName}`;
    }
    const real = card(
      h(
        'div',
        { class: 'profile-head' },
        avatarEl(name === 'Fiche incomplète' ? '' : name, me.circleId, 68, { ring: true }),
        h(
          'div',
          { class: 'profile-id' },
          h('div', { class: 'profile-name', text: name }),
          h('div', { class: 'small muted', text: p.email || 'Ajoutez vos coordonnées' }),
        ),
      ),
      idField('Date de naissance', formatDob(p.dob)),
      idField('Téléphone', p.phone),
      ...p.addresses.map((a, i) => idField(addressLabel(a, i, p.addresses.length), addressText(a))),
      ghostButton('Modifier ma fiche', () => navigate({ name: 'editProfile' }), 'edit'),
    );
    profileSlot.replaceWith(real);
  });

  const notices: HTMLElement[] = [];
  if (!me.seedBackedUp) {
    // Only reachable by leaving onboarding at the recovery-phrase step: the
    // phrase is never stored, so it genuinely cannot be shown again. Say so, and
    // point at the only real fix while the account is still empty.
    notices.push(
      notice(
        'warn',
        'Vous n’avez pas noté votre phrase de récupération, et elle ne peut plus être affichée. Si vous perdez cet appareil, votre Cercle est perdu avec lui. Pour repartir sur une nouvelle identité : Réglages, puis « Effacer les données ».',
      ),
    );
  }
  const storage = lastStorageStatus();
  if (storage.supported && !storage.persisted && !isStandalone()) {
    notices.push(
      notice(
        'warn',
        'Votre navigateur peut effacer les données de Cercle pour faire de la place. Installez l’app sur votre écran d’accueil pour l’en empêcher.',
        ghostButton('Installer l’app', openInstallSheet, 'download'),
      ),
    );
  }

  return appShell('dashboard', header, introCard, profileSlot, ...notices);
}

// --- Settings ---------------------------------------------------------------

export function SettingsScreen(): HTMLElement {
  const me = currentIdentity();
  if (!me) return WelcomeScreen();

  const dangerBtn = h('button', {
    class: 'btn btn-danger-quiet',
    onclick: async () => {
      const ok = confirm(
        'Toutes les données de Cercle vont être effacées de cet appareil. Sans votre phrase de récupération, l’accès est définitivement perdu. Continuer ?',
      );
      if (!ok) return;
      await wipeLocal();
      navigate({ name: 'welcome' });
    },
  }) as HTMLButtonElement;
  dangerBtn.append(icon('trash', 18), document.createTextNode('Effacer les données de cet appareil'));

  // Checks every contact against exactly what publishing needs from it — a
  // decodable Circle ID and a public key that actually lies on the curve —
  // so a contact flagged here is one that can never receive an update no
  // matter what (bad local data, not a network/relay problem). Surfacing it
  // beats a silent "profil en attente d'envoi" the user can't act on.
  const diagSlot = h('div', {}, h('p', { class: 'small muted', text: 'Vérification de vos proches…' }));
  const runDiagnostic = (): void => {
    diagSlot.replaceChildren(h('p', { class: 'small muted', text: 'Vérification de vos proches…' }));
    void (async () => {
      const friends = await listFriends();
      const broken: Friend[] = [];
      for (const f of friends) {
        try {
          const decoded = decodeCircleId(f.circleId);
          await importEcdhPublic(decoded.ecdhPublicRaw);
        } catch {
          broken.push(f);
        }
      }

      if (broken.length === 0) {
        diagSlot.replaceChildren(
          h('p', {
            class: 'small muted',
            text:
              friends.length === 0
                ? 'Aucun proche à vérifier.'
                : `${friends.length} proche${friends.length > 1 ? 's' : ''} vérifié${friends.length > 1 ? 's' : ''}, aucun problème détecté.`,
          }),
        );
        return;
      }

      const rows = broken.map((f) =>
        h(
          'div',
          { class: 'group-manage-row' },
          h('span', { class: 'small group-manage-row-label', text: shortId(f.circleId) }),
          h(
            'button',
            {
              class: 'icon-btn',
              'aria-label': 'Retirer ce proche',
              title: 'Retirer ce proche',
              onclick: async () => {
                if (
                  !confirm(
                    'Retirer ce proche ? Son ID Cercle est abîmé : il ne peut de toute façon pas recevoir vos mises à jour, et il empêche les autres de recevoir les leurs.',
                  )
                )
                  return;
                await revokeFriend(me, f.circleId);
                toast('Proche retiré');
                runDiagnostic();
              },
            },
            icon('trash', 18),
          ),
        ),
      );

      diagSlot.replaceChildren(
        notice(
          'warn',
          broken.length === 1
            ? 'Un proche a un ID Cercle abîmé. Tant qu’il est là, votre fiche ne part chez personne : retirez-le.'
            : `${broken.length} proches ont un ID Cercle abîmé. Tant qu’ils sont là, votre fiche ne part chez personne : retirez-les.`,
        ),
        h('div', {}, ...rows),
      );
    })();
  };
  runDiagnostic();

  const bmcBtn = h(
    'button',
    {
      class: 'bmc-btn',
      onclick: () => window.open('https://www.buymeacoffee.com/blaviale', '_blank', 'noopener'),
    },
    icon('coffee', 16),
    h('span', { text: 'Buy me a coffee' }),
  ) as HTMLButtonElement;

  return appShell(
    'dashboard',
    pageHeader('Réglages', { name: 'dashboard' }, bmcBtn),
    card(
      sectionTitle('Comment ça marche'),
      h('p', { class: 'small muted', text: 'Ce qu’est Cercle, comment ajouter un proche, et ce que veulent dire votre phrase de récupération et votre empreinte de vérification.' }),
      ghostButton('Voir le fonctionnement', openHowItWorksSheet, 'info'),
    ),
    isStandalone()
      ? false
      : card(
          sectionTitle('Installation'),
          h('p', {
            class: 'small muted',
            text: 'Ajoutez Cercle à votre écran d’accueil : vos données y sont mieux protégées et l’app s’ouvre en plein écran.',
          }),
          ghostButton('Installer l’app', openInstallSheet, 'download'),
        ),
    card(
      sectionTitle('Groupes'),
      h('p', { class: 'small muted', text: 'Organisez vos proches en groupes (Famille, Voisins, Collègues…).' }),
      ghostButton('Gérer les groupes', () => navigate({ name: 'groups' }), 'users'),
    ),
    card(
      sectionTitle('Diagnostic'),
      h('p', { class: 'small muted', text: 'Vérifie que vos proches peuvent bien recevoir votre fiche.' }),
      diagSlot,
      ghostButton('Revérifier', runDiagnostic, 'refresh'),
    ),
    h(
      'div',
      { class: 'card card-sensitive' },
      h('p', { class: 'eyebrow eyebrow-danger', text: 'Zone sensible' }),
      h('p', {
        class: 'small muted',
        text: 'Efface toutes les données de Cercle sur cet appareil. Sans votre phrase de récupération, l’accès est définitivement perdu.',
      }),
      dangerBtn,
    ),
  );
}

// --- Groups management ------------------------------------------------------

// The group name is an inline editable field (renames on blur / Enter), so
// there are no native prompt() popups anywhere in the flow.
function groupManageRow(g: Group, count: number, refresh: () => void): HTMLElement {
  const nameInput = h('input', {
    value: g.name,
    class: 'group-name-input',
    'aria-label': 'Nom du groupe',
    autocomplete: 'off',
    spellcheck: 'false',
  }) as HTMLInputElement;

  let saving = false;
  const save = async (): Promise<void> => {
    if (saving) return;
    const next = nameInput.value.trim();
    if (!next || next === g.name) {
      nameInput.value = g.name;
      return;
    }
    saving = true;
    try {
      await renameGroup(g.id, next);
      const me = currentIdentity();
      if (me) void publishSelfBackup(me);
      refresh();
    } catch (e) {
      toast((e as Error).message);
      nameInput.value = g.name;
      saving = false;
    }
  };
  nameInput.addEventListener('blur', () => void save());
  nameInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      nameInput.blur();
    } else if (e.key === 'Escape') {
      nameInput.value = g.name;
      nameInput.blur();
    }
  });

  const del = h(
    'button',
    {
      class: 'icon-btn',
      'aria-label': 'Supprimer',
      title: 'Supprimer',
      onclick: async () => {
        if (!confirm(`Supprimer le groupe « ${g.name} » ? Vos proches ne sont pas supprimés, seulement retirés du groupe.`)) return;
        await deleteGroup(g.id);
        const me = currentIdentity();
        if (me) void publishSelfBackup(me);
        refresh();
      },
    },
    icon('trash', 18),
  );

  return h(
    'div',
    { class: 'group-manage-row' },
    nameInput,
    h('span', { class: 'group-manage-count', text: count === 1 ? '1' : String(count) }),
    del,
  );
}

export function GroupsScreen(): HTMLElement {
  const me = currentIdentity();
  if (!me) return WelcomeScreen();

  const listSlot = h('div', {}, skeletonCard(3));
  const refresh = (): void => {
    void (async () => {
      const [groups, counts] = await Promise.all([listGroups(), groupCounts()]);
      const body = groups.length
        ? h('div', {}, ...groups.map((g) => groupManageRow(g, counts.get(g.id) ?? 0, refresh)))
        : h('p', { class: 'small muted', text: 'Aucun groupe pour l’instant. Créez-en un ci-dessous pour organiser vos proches.' });
      listSlot.replaceChildren(card(sectionTitle('Mes groupes'), body));
    })();
  };
  refresh();

  // Inline "new group" field — no popup, type a name and press Enter / Ajouter.
  const newInput = h('input', {
    type: 'text',
    placeholder: 'Nom du nouveau groupe',
    'aria-label': 'Nouveau groupe',
    autocomplete: 'off',
    spellcheck: 'false',
  }) as HTMLInputElement;
  const addGroup = async (): Promise<void> => {
    const name = newInput.value.trim();
    if (!name) return;
    try {
      await createGroup(name);
      void publishSelfBackup(me);
      newInput.value = '';
      refresh();
    } catch (e) {
      toast((e as Error).message);
    }
  };
  newInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void addGroup();
    }
  });
  const addBtn = h('button', { class: 'btn btn-primary', type: 'button', onclick: () => void addGroup() }) as HTMLButtonElement;
  addBtn.append(icon('plus', 18), document.createTextNode('Ajouter'));
  const addCard = card(sectionTitle('Nouveau groupe'), h('div', { class: 'group-add-row' }, newInput, addBtn));

  return appShell('dashboard', pageHeader('Groupes', { name: 'settings' }), listSlot, addCard);
}

// --- Share ID ---------------------------------------------------------------

export function ShareIdScreen(): HTMLElement {
  const me = currentIdentity();
  if (!me) return WelcomeScreen();

  return appShell('shareId', mainHeader('Partager', 'Votre lien'), shareCard(me.circleId));
}

// --- Invite (landing from a shared link) ------------------------------------

/**
 * Landing screen for a shared invite link (`#/inviter/<circleId>`). Reachable
 * with or without an identity. With one, it routes straight to adding the
 * sender; without one, it remembers the invite and hands over to onboarding,
 * which adapts its copy to "someone invited you" and resumes the add at the end.
 */
export function InviteScreen(circleId: string): HTMLElement {
  const raw = circleId.trim();
  let valid = true;
  try {
    decodeCircleId(raw);
  } catch {
    valid = false;
  }

  if (!valid) {
    return centered(
      brand('lg'),
      h('h1', { class: 'hero-title', text: 'Lien d’invitation invalide' }),
      lead('Ce lien ne contient pas d’ID Cercle valide. Demandez à votre proche de vous le renvoyer.'),
      primaryButton('Continuer', () => navigate({ name: 'welcome' })),
    );
  }

  const me = currentIdentity();

  if (me && raw === me.circleId) {
    return centered(
      brand('lg'),
      h('h1', { class: 'hero-title', text: 'Votre lien d’invitation' }),
      lead('Ceci est votre propre lien. Partagez-le pour qu’on vous ajoute.'),
      card(
        primaryButton('Partager mon lien', () => navigate({ name: 'shareId' }), 'share'),
        ghostButton('Aller à mon profil', () => navigate({ name: 'dashboard' })),
      ),
    );
  }

  // Remember the target either way; the Cercle screen (identity in hand) or the
  // end of onboarding (no identity yet) consumes it and prefills the add sheet.
  setPendingInvite(raw);
  void Promise.resolve().then(() => navigate(me ? { name: 'circle' } : { name: 'welcome' }));
  return centered(brand('lg'), lead('Ouverture…'));
}

// --- Cercle (friends list) --------------------------------------------------

type LinkStatus = 'verified' | 'connected' | 'pending';

function linkStatus(f: Friend, received?: ProfileRecord): LinkStatus {
  if (f.verified) return 'verified';
  return received ? 'connected' : 'pending';
}

function statusSubtitle(status: LinkStatus, received?: ProfileRecord): string {
  if (status === 'pending') return 'En attente';
  // Verified vs. simply connected is already conveyed by the dot color, so
  // the subtitle for either instead surfaces when their profile last arrived.
  if (received) return `Reçu le ${new Date(received.updatedAt).toLocaleDateString()}`;
  return status === 'verified' ? 'Vérifié·e' : 'Connecté·e';
}

/**
 * What to call a friend in the list: the first+last name they shared once their
 * profile has arrived, else the local placeholder set when adding them (so a
 * still-pending contact reads as "Mamie" rather than a raw ID stub), else the
 * short ID stub as a last resort. The shared name always wins once it arrives —
 * everyone still ends up seeing the same, owner-controlled name; the temp name
 * is only ever local scaffolding for while a contact hasn't synced back yet.
 */
function friendDisplayName(f: Friend, received?: ProfileRecord): string {
  const shared = received ? fullName(received.profile) : '';
  return shared || f.tempName || shortId(f.circleId);
}

type Row = { f: Friend; received: ProfileRecord | undefined; name: string };

function friendRow(row: Row): HTMLElement {
  const status = linkStatus(row.f, row.received);
  // The green dot means "manually verified", nothing else; pending is amber, a
  // plain connection is neutral. The subtitle spells the same status out.
  const dotClass = status === 'verified' ? ' is-verified' : status === 'pending' ? ' is-pending' : '';
  const rowAvatar = h('div', { class: 'row-avatar' }, avatarEl(row.name, row.f.circleId, 40), h('span', { class: 'status-dot' + dotClass }));
  return h(
    'button',
    { class: 'friend-row', onclick: () => navigate({ name: 'friendDetail', circleId: row.f.circleId }) },
    rowAvatar,
    h(
      'div',
      { class: 'friend-meta' },
      h('div', { class: 'friend-name', text: row.name }),
      h('div', { class: 'friend-sub', text: statusSubtitle(status, row.received) }),
    ),
    icon('back', 18),
  );
}

function friendSection(title: string, rows: Row[]): HTMLElement {
  return h(
    'section',
    { class: 'friend-group' },
    h('h2', { class: 'friend-group-title' }, h('span', { text: title }), h('span', { class: 'friend-group-count', text: String(rows.length) })),
    h('div', { class: 'friend-list' }, ...rows.map(friendRow)),
  );
}

function emptyList(text: string): HTMLElement {
  return h('div', { class: 'card empty-state' }, icon('users', 28), h('p', { text }));
}

/** Guided empty state for a brand-new Cercle — the very first thing a new
 * user sees on this tab, before they've ever opened the add-friend sheet. The
 * other empty cases (search, group, pending) stay on the terser `emptyList`. */
function firstRunEmptyState(onAdd: () => void): HTMLElement {
  return card(
    h('p', {
      class: 'lead',
      text: 'Votre Cercle est vide. Pour échanger vos coordonnées avec quelqu’un, il faut que chacun ajoute l’autre :',
    }),
    h(
      'ul',
      { class: 'onb-bullets' },
      bullet('qr', 'Ajoutez son ID Cercle', 'Scannez son QR code, ou collez l’ID Cercle qu’il vous a envoyé.'),
      bullet('share', 'Donnez-lui le vôtre', 'Depuis l’onglet Partager, envoyez-lui votre QR code ou votre lien : sans ça, il ne peut pas vous ajouter.'),
      bullet('refresh', 'Vos fiches s’échangent', 'Dès que chacun a ajouté l’autre, vos coordonnées circulent et restent à jour toutes seules.'),
    ),
    primaryButton('Ajouter un proche', onAdd, 'plus'),
  );
}

export function CircleScreen(): HTMLElement {
  const searchInput = h('input', {
    type: 'text',
    placeholder: 'Rechercher un proche…',
    autocapitalize: 'none',
    autocomplete: 'off',
    spellcheck: 'false',
    'aria-label': 'Rechercher un proche',
  }) as HTMLInputElement;
  const searchBar = h('div', { class: 'search-bar' }, icon('search', 18), searchInput);
  const chipsRow = h('div', { class: 'chips' });
  const listSlot = h('div', {}, skeletonCard(2));
  const header = mainHeader('Votre Cercle', 'Votre Cercle', syncIndicator());

  let rows: Row[] = [];
  let groups: Group[] = [];
  let active = 'all'; // 'all' | <groupId> | 'pending'
  let query = '';

  const byName = (a: Row, b: Row): number => a.name.localeCompare(b.name);
  const filtered = (): Row[] => {
    const q = query.trim().toLowerCase();
    return q ? rows.filter((r) => r.name.toLowerCase().includes(q)) : rows;
  };

  const renderChips = (): void => {
    const mk = (label: string, key: string): HTMLElement =>
      h(
        'button',
        {
          class: 'chip-filter' + (active === key ? ' is-active' : ''),
          type: 'button',
          onclick: () => {
            active = key;
            renderChips();
            renderList();
          },
        },
        label,
      );
    chipsRow.replaceChildren(mk('Tous', 'all'), ...groups.map((g) => mk(g.name, g.id)), mk('En attente', 'pending'));
  };

  const renderList = (): void => {
    const rs = filtered();
    if (!rows.length) {
      listSlot.replaceChildren(firstRunEmptyState(() => openAdd()));
      return;
    }
    if (active === 'pending') {
      const pend = rs.filter((r) => !r.received).sort(byName);
      listSlot.replaceChildren(pend.length ? h('div', { class: 'friend-list' }, ...pend.map(friendRow)) : emptyList('Aucun proche en attente.'));
      return;
    }
    if (active !== 'all') {
      const gm = rs.filter((r) => (r.f.groups ?? []).includes(active)).sort(byName);
      listSlot.replaceChildren(gm.length ? h('div', { class: 'friend-list' }, ...gm.map(friendRow)) : emptyList('Aucun proche dans ce groupe.'));
      return;
    }
    // "Tous": a section per group (only non-empty), then everyone ungrouped.
    const sections: HTMLElement[] = [];
    for (const g of groups) {
      const gm = rs.filter((r) => (r.f.groups ?? []).includes(g.id)).sort(byName);
      if (gm.length) sections.push(friendSection(g.name, gm));
    }
    const ungrouped = rs.filter((r) => !(r.f.groups && r.f.groups.length)).sort(byName);
    if (ungrouped.length) sections.push(friendSection(groups.length ? 'Sans groupe' : 'Mes proches', ungrouped));
    listSlot.replaceChildren(sections.length ? h('div', { class: 'friend-groups' }, ...sections) : emptyList('Aucun résultat.'));
  };

  const reload = (): void => {
    void (async () => {
      const friends = await listFriends();
      const profiles = await Promise.all(friends.map((f) => getCachedProfile(f.circleId)));
      rows = friends.map((f, i) => ({ f, received: profiles[i], name: friendDisplayName(f, profiles[i]) }));
      groups = await listGroups();
      const pending = rows.filter((r) => !r.received).length;
      const eb = header.querySelector('.eyebrow');
      if (eb) {
        const total = rows.length;
        eb.textContent = `${total} proche${total > 1 ? 's' : ''}${pending ? ` · ${pending} en attente` : ''}`;
      }
      renderChips();
      renderList();
    })();
  };

  const openAdd = (prefill = ''): void => openAddFriendSheet(prefill, reload);

  reload();
  // Resume a shared invite. A brand-new Circle ID still has something to
  // decide — at least whether to name this person — so it opens the sheet
  // prefilled, same as before. But if they're already a friend, reopening
  // their link is the reciprocal confirmation with nothing left to name:
  // just refresh the connection, no sheet, no button.
  const invited = takePendingInvite();
  if (invited) {
    void (async () => {
      if (await getFriend(invited)) {
        // requestSync() swallows relay failures into the sync indicator
        // rather than throwing, so there's nothing to catch here.
        await requestSync();
        toast('Connexion à jour');
        reload();
      } else {
        requestAnimationFrame(() => openAdd(invited));
      }
    })();
  }

  searchInput.addEventListener('input', () => {
    query = searchInput.value;
    renderList();
  });

  const fab = h(
    'button',
    { class: 'fab', type: 'button', 'aria-label': 'Ajouter un proche', title: 'Ajouter un proche', onclick: () => openAdd() },
    icon('plus', 26),
  );

  const shell = appShell('circle', header, searchBar, chipsRow, listSlot);
  // The FAB is a direct child of the shell (not the content column), so its
  // fixed positioning anchors to the viewport and is never re-anchored by the
  // content column's enter transform.
  shell.append(fab);
  // Pull down on the contacts list to force a sync.
  return withPullToRefresh(shell, () => requestSync());
}

// --- Friend detail ----------------------------------------------------------

export function FriendDetailScreen(circleId: string): HTMLElement {
  const me = currentIdentity();
  if (!me) return WelcomeScreen();

  const headSlot = skeletonCard(2);
  const contactSlot = skeletonCard(4);
  const groupsSlot = skeletonCard(2);
  const verifySlot = skeletonCard(3);
  const dangerSlot = h('div', { class: 'danger-zone' });

  void (async () => {
    const friend = await getFriend(circleId);
    if (!friend) {
      navigate({ name: 'circle' });
      return;
    }
    const [fingerprint, cached] = await Promise.all([
      computeFingerprint(me.circleId, friend.circleId),
      getCachedProfile(friend.circleId),
    ]);

    const displayName = friendDisplayName(friend, cached);
    headSlot.replaceWith(
      card(
        h(
          'div',
          { class: 'profile-head' },
          avatarEl(displayName, friend.circleId, 56),
          h(
            'div',
            { class: 'profile-id' },
            h('div', { class: 'profile-name', text: displayName }),
            h('div', {
              class: 'small muted',
              text: cached
                ? 'Connectés : vous vous êtes ajoutés tous les deux'
                : 'En attente : cette personne doit encore vous ajouter de son côté',
            }),
          ),
        ),
      ),
    );

    if (cached) {
      const cp = cached.profile;
      contactSlot.replaceWith(
        card(
          sectionTitle('Coordonnées'),
          field('Nom', fullName(cp)),
          field('Email', cp.email),
          field('Téléphone', cp.phone),
          field('Date de naissance', formatDob(cp.dob)),
          ...cp.addresses.map((a, i) => field(addressLabel(a, i, cp.addresses.length), addressText(a))),
          h('div', { class: 'small muted', text: `Reçu le ${new Date(cached.updatedAt).toLocaleDateString()}` }),
        ),
      );
    } else {
      // No profile yet: explain why, and right there let the owner set (or
      // edit) a local placeholder name to tell this contact apart in the list
      // in the meantime — it's never shared, and gives way the moment the
      // real name arrives.
      const nameInput = h('input', {
        type: 'text',
        placeholder: 'Ex. Mamie, Collègue du bureau…',
        maxlength: '60',
        autocomplete: 'off',
        spellcheck: 'false',
        'aria-label': 'Nom temporaire',
      }) as HTMLInputElement;
      nameInput.value = friend.tempName ?? '';
      let saved = friend.tempName ?? '';
      const commit = async (): Promise<void> => {
        const value = nameInput.value.trim();
        if (value === saved) return;
        saved = value;
        await setTempName(friend.circleId, value);
        void publishSelfBackup(me);
      };
      nameInput.addEventListener('blur', () => void commit());
      nameInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') nameInput.blur();
      });
      contactSlot.replaceWith(
        card(
          h('p', {
            class: 'small muted',
            text: 'Ses coordonnées apparaîtront dès qu’il vous aura ajouté de son côté. Pensez à lui envoyer votre ID Cercle depuis l’onglet Partager.',
          }),
          h(
            'label',
            { class: 'form-field' },
            h('span', { class: 'field-label', text: 'Nom temporaire, en attendant' }),
            nameInput,
          ),
        ),
      );
    }

    // Group membership: toggle this contact in/out of any group, or spin up a
    // new group on the spot. A contact can be in zero, one, or several groups.
    let allGroups = await listGroups();
    let current = friend.groups ?? [];
    const picker = h('div', { class: 'group-picker' });
    const renderPicker = (): void => {
      const toggles = allGroups.map((g) => {
        const on = current.includes(g.id);
        return h(
          'button',
          {
            class: 'group-toggle' + (on ? ' is-on' : ''),
            type: 'button',
            onclick: async () => {
              current = on ? current.filter((x) => x !== g.id) : [...current, g.id];
              await setFriendGroups(friend.circleId, current);
              void publishSelfBackup(me);
              renderPicker();
            },
          },
          g.name,
        );
      });
      // "+ Nouveau groupe" expands into an inline field (no popup): type a name,
      // Enter creates the group and tags this contact into it; Escape cancels.
      const add = h('button', { class: 'group-toggle group-add', type: 'button' }, '+ Nouveau groupe');
      add.addEventListener('click', () => {
        const input = h('input', {
          class: 'group-new-input',
          placeholder: 'Nom du groupe',
          'aria-label': 'Nouveau groupe',
          autocomplete: 'off',
          spellcheck: 'false',
        }) as HTMLInputElement;
        let done = false;
        const commit = async (): Promise<void> => {
          if (done) return;
          const name = input.value.trim();
          if (!name) {
            done = true;
            renderPicker();
            return;
          }
          done = true;
          try {
            const g = await createGroup(name);
            allGroups = [...allGroups, g];
            current = [...current, g.id];
            await setFriendGroups(friend.circleId, current);
            void publishSelfBackup(me);
            renderPicker();
          } catch (e) {
            toast((e as Error).message);
            done = false;
            input.focus();
          }
        };
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void commit();
          } else if (e.key === 'Escape') {
            done = true;
            renderPicker();
          }
        });
        input.addEventListener('blur', () => void commit());
        add.replaceWith(input);
        input.focus();
      });
      picker.replaceChildren(...toggles, add);
    };
    renderPicker();
    groupsSlot.replaceWith(
      card(
        sectionTitle('Groupes'),
        h('p', {
          class: 'small muted',
          text: allGroups.length
            ? 'Rangez ce proche dans un ou plusieurs groupes — ou aucun.'
            : 'Créez un groupe pour organiser vos proches (Famille, Voisins, Collègues…).',
        }),
        picker,
      ),
    );

    const verifyBtn = friend.verified
      ? ghostButton('Retirer la vérification', async () => {
          await setVerified(friend.circleId, false);
          void publishSelfBackup(me);
          navigate({ name: 'friendDetail', circleId: friend.circleId });
        })
      : primaryButton('Marquer comme vérifié', async () => {
          await setVerified(friend.circleId, true);
          void publishSelfBackup(me);
          navigate({ name: 'friendDetail', circleId: friend.circleId });
        }, 'shield');
    verifySlot.replaceWith(
      card(
        sectionTitle('Empreinte de vérification'),
        h('p', {
          class: 'small muted',
          text:
            'Lisez ces chiffres à votre proche de vive voix : s’il voit exactement les mêmes, c’est bien lui. Utile surtout si vous l’avez ajouté à distance.',
        }),
        h('div', { class: 'id-box mono center', text: fingerprint }),
        h('div', { class: 'id-box mono small muted', text: friend.circleId }),
        verifyBtn,
      ),
    );

    const removeBtn = h('button', {
      class: 'btn btn-danger',
      onclick: async () => {
        if (
          !confirm(
            'Retirer ce proche ? Il ne recevra plus aucune mise à jour et sa copie de vos coordonnées sera effacée. Cette action est irréversible.',
          )
        )
          return;
        await revokeFriend(me, friend.circleId);
        toast('Proche retiré');
        navigate({ name: 'circle' });
      },
    }) as HTMLButtonElement;
    removeBtn.append(icon('trash', 18), document.createTextNode('Retirer ce proche'));
    dangerSlot.append(removeBtn);
  })();

  return appShell('circle', pageHeader('Détail du proche', { name: 'circle' }), headSlot, contactSlot, groupsSlot, verifySlot, dangerSlot);
}

// --- Edit profile -----------------------------------------------------------

const PROFILE_DRAFT = 'profile';

interface ProfileDraft {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  dob?: string;
  addresses?: Address[];
}

export function EditProfileScreen(): HTMLElement {
  const formSlot = skeletonCard(6);

  void getMyRecord().then((record) => {
    const saved: Profile = { ...emptyProfile(), ...record.profile };
    const draft = loadDraft<ProfileDraft>(PROFILE_DRAFT);
    const val = (k: keyof ProfileDraft, fallback: string) => (draft?.[k] as string | undefined) ?? fallback;

    const inputs = new Map<string, HTMLInputElement>();
    function textField(key: string, label: string, value: string, type = 'text', extra: Record<string, string> = {}): HTMLElement {
      const input = h('input', { type, value, autocomplete: 'off', ...extra }) as HTMLInputElement;
      inputs.set(key, input);
      return h('label', { class: 'form-field' }, h('span', { class: 'field-label', text: label }), input);
    }

    // Each address is its own free-form block (label + text + country) — a
    // second home, a work address, a parents' place — added and removed
    // independently, so no fixed count or shape is imposed.
    let addressCards: { el: HTMLElement; get: () => Address }[] = [];
    const addressList = h('div', { class: 'stack address-list' });

    function saveDraftNow(): void {
      const scalars: Record<string, string> = {};
      for (const [k, el] of inputs) scalars[k] = el.value;
      saveDraft(PROFILE_DRAFT, { ...scalars, addresses: addressCards.map((c) => c.get()) });
    }

    function addAddressCard(addr: Address): void {
      const label = h('input', {
        type: 'text',
        value: addr.label,
        placeholder: 'Domicile, travail…',
        autocomplete: 'off',
      }) as HTMLInputElement;
      const lines = h('textarea', {
        rows: '3',
        placeholder: 'Numéro, rue, ville, code postal…',
        autocomplete: 'off',
      }) as HTMLTextAreaElement;
      lines.value = addr.lines;
      const country = h('input', { type: 'text', value: addr.country, placeholder: 'Pays', autocomplete: 'off' }) as HTMLInputElement;
      for (const el of [label, lines, country]) el.addEventListener('input', saveDraftNow);

      const entry = {
        el: card(
          h('label', { class: 'form-field' }, h('span', { class: 'field-label', text: 'Étiquette' }), label),
          h('label', { class: 'form-field' }, h('span', { class: 'field-label', text: 'Adresse' }), lines),
          h('label', { class: 'form-field' }, h('span', { class: 'field-label', text: 'Pays' }), country),
        ),
        get: (): Address => ({ label: label.value.trim(), lines: lines.value.trim(), country: country.value.trim() }),
      };
      entry.el.append(
        ghostButton(
          'Retirer cette adresse',
          () => {
            addressCards = addressCards.filter((c) => c !== entry);
            entry.el.remove();
            saveDraftNow();
          },
          'trash',
        ),
      );
      addressCards.push(entry);
      addressList.append(entry.el);
    }

    for (const a of draft?.addresses ?? saved.addresses) addAddressCard(a);

    const addAddressBtn = ghostButton(
      'Ajouter une adresse',
      () => {
        addAddressCard({ label: '', lines: '', country: '' });
        saveDraftNow();
      },
      'plus',
    );

    const form = card(
      h(
        'div',
        { class: 'row' },
        textField('firstName', 'Prénom', val('firstName', saved.firstName)),
        textField('lastName', 'Nom', val('lastName', saved.lastName)),
      ),
      textField('dob', 'Date de naissance', val('dob', dobToDisplay(saved.dob)), 'text', {
        placeholder: 'JJ/MM/AAAA',
        inputmode: 'numeric',
        pattern: '\\d{2}/\\d{2}/\\d{4}',
        maxlength: '10',
        title: 'Format : JJ/MM/AAAA',
      }),
      textField('email', 'Email', val('email', saved.email), 'email'),
      textField('phone', 'Téléphone', val('phone', saved.phone), 'tel'),
    );
    const dobInput = inputs.get('dob');
    if (dobInput) {
      dobInput.addEventListener('input', () => {
        const pos = dobInput.selectionStart;
        const prevLen = dobInput.value.length;
        dobInput.value = formatDobInput(dobInput.value);
        const diff = dobInput.value.length - prevLen;
        if (pos !== null) dobInput.setSelectionRange(pos + diff, pos + diff);
      });
    }
    for (const [, el] of inputs) el.addEventListener('input', saveDraftNow);

    const save = primaryButton('Enregistrer', async () => {
      const get = (k: string) => inputs.get(k)!.value.trim();
      const next: Profile = {
        firstName: get('firstName'),
        lastName: get('lastName'),
        email: get('email'),
        phone: get('phone'),
        dob: dobFromDisplay(get('dob')),
        addresses: addressCards.map((c) => c.get()).filter((a) => a.label || a.lines || a.country),
      };
      await saveMyProfile(next);
      clearDraft(PROFILE_DRAFT);
      toast('Fiche enregistrée');
      const me = currentIdentity();
      if (me) {
        try {
          const { published } = await publishMyProfile(me);
          if (published > 0) toast(`Fiche envoyée à ${published} proche${published > 1 ? 's' : ''}`);
        } catch {
          // Relay unreachable — the local save still succeeded, and it's now
          // queued: the sync manager retries automatically on reconnect.
          toast('Fiche enregistrée — elle partira dès la reconnexion');
        }
      }
      navigate({ name: 'dashboard' });
    }, 'check');

    const cancel = ghostButton('Annuler', () => {
      clearDraft(PROFILE_DRAFT);
      navigate({ name: 'dashboard' });
    });

    const restored = draft ? notice('warn', 'Brouillon non enregistré restauré.') : null;
    formSlot.replaceWith(
      h('div', { class: 'stack' }, restored, form, sectionTitle('Adresses'), addressList, addAddressBtn, save, cancel),
    );
  });

  return appShell(
    'dashboard',
    pageHeader('Modifier ma fiche', { name: 'dashboard' }),
    lead('Ces informations partent, chiffrées, chez les proches qui vous ont ajouté de leur côté.'),
    formSlot,
  );
}
